import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import { useAuth } from '../../context/AuthContext';
import {
  createActivityNameMap,
  getActivities,
} from '../../services/activityService';
import {
  createTrainingClassMap,
  getTrainingClasses,
} from '../../services/trainingClassService';
import { getTrainingSessions } from '../../services/trainingSessionService';
import DashboardChartCard from './DashboardChartCard';
import {
  CLASS_ACTIVITY_LINE_DEFAULT_LIMIT,
  buildClassActivityTotals,
  buildClassActivityTrainingByPeriod,
  buildClassOptions,
  buildClassStats,
  buildClassTrainingByPeriod,
  durationOptions,
  filterSessionsByDuration,
  isMonthlyGroupingDisabled,
} from './dashboardOverviewUtils';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
);

function DashboardClasses({
  period,
  duration,
  setPeriod,
  setDuration,
}) {
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [hasCustomizedActivities, setHasCustomizedActivities] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activityNamesById, setActivityNamesById] = useState({});
  const [trainingClassesByName, setTrainingClassesByName] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { accessToken, logout } = useAuth();
  const theme = useTheme();
  const chartColors = useMemo(
    () => theme.customTokens?.chartPalette || [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      '#2a9d8f',
      '#e9c46a',
      '#f4a261',
      '#e76f51',
      '#64748b',
    ],
    [theme]
  );

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      logout({ shouldCallApi: false });
      return;
    }

    const loadDashboardData = async () => {
      try {
        const [sessionsData, activitiesData, trainingClassesData] = await Promise.all([
          getTrainingSessions(accessToken),
          getActivities(accessToken),
          getTrainingClasses(accessToken),
        ]);

        const activities = Array.isArray(activitiesData) ? activitiesData : [];
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        setActivityNamesById(createActivityNameMap(activities));
        setTrainingClassesByName(createTrainingClassMap(Array.isArray(trainingClassesData) ? trainingClassesData : []));
      } catch (err) {
        if (err?.status === 401) {
          await logout({ shouldCallApi: false });
          return;
        }

        setError(err.message || 'Failed to load class dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [accessToken, logout]);

  const filteredSessions = useMemo(
    () => filterSessionsByDuration(sessions, duration),
    [duration, sessions]
  );
  const classOptions = useMemo(
    () => buildClassOptions(filteredSessions, trainingClassesByName),
    [filteredSessions, trainingClassesByName]
  );

  useEffect(() => {
    if (classOptions.length === 0) {
      setSelectedClassId('');
      return;
    }

    const availableClassIds = new Set(classOptions.map((trainingClass) => trainingClass.id));
    const defaultClassId = availableClassIds.has('all_school')
      ? 'all_school'
      : classOptions[0].id;

    setSelectedClassId((currentClassId) => (
      availableClassIds.has(currentClassId)
        ? currentClassId
        : defaultClassId
    ));
  }, [classOptions]);

  const selectedClassName = useMemo(
    () => classOptions.find((trainingClass) => trainingClass.id === selectedClassId)?.name || 'Class',
    [classOptions, selectedClassId]
  );
  const classTrainingByPeriod = useMemo(
    () => buildClassTrainingByPeriod(filteredSessions, period, selectedClassId),
    [filteredSessions, period, selectedClassId]
  );
  const classActivityTotals = useMemo(
    () => buildClassActivityTotals(filteredSessions, selectedClassId, activityNamesById),
    [activityNamesById, filteredSessions, selectedClassId]
  );
  const classActivityTrainingByPeriod = useMemo(
    () => buildClassActivityTrainingByPeriod(filteredSessions, period, selectedClassId, activityNamesById),
    [activityNamesById, filteredSessions, period, selectedClassId]
  );
  const classStats = useMemo(
    () => buildClassStats(filteredSessions, selectedClassId),
    [filteredSessions, selectedClassId]
  );
  const activityOptions = useMemo(
    () => classActivityTrainingByPeriod.activities.map(([activityName]) => activityName),
    [classActivityTrainingByPeriod.activities]
  );
  const defaultActivities = useMemo(
    () => activityOptions.slice(0, CLASS_ACTIVITY_LINE_DEFAULT_LIMIT),
    [activityOptions]
  );

  useEffect(() => {
    if (activityOptions.length === 0) {
      setSelectedActivities([]);
      return;
    }

    setSelectedActivities((currentActivities) => {
      if (!hasCustomizedActivities) {
        return defaultActivities;
      }

      const availableActivities = new Set(activityOptions);
      const retainedActivities = currentActivities.filter((activity) => availableActivities.has(activity));
      return retainedActivities.length > 0 ? retainedActivities : defaultActivities;
    });
  }, [activityOptions, defaultActivities, hasCustomizedActivities]);

  const selectedActivitySet = useMemo(
    () => new Set(selectedActivities),
    [selectedActivities]
  );
  const selectedActivitySeries = useMemo(
    () => classActivityTrainingByPeriod.activities
      .filter(([activityName]) => selectedActivitySet.has(activityName)),
    [classActivityTrainingByPeriod.activities, selectedActivitySet]
  );

  const baseChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
        },
      },
      tooltip: {
        backgroundColor: theme.customTokens?.chartTooltipBg,
        titleColor: theme.customTokens?.chartTooltipText,
        bodyColor: theme.customTokens?.chartTooltipText,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${Number(context.raw) || 0} min`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          maxRotation: 0,
          autoSkip: true,
        },
        grid: {
          color: theme.customTokens?.chartGrid || theme.palette.divider,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          callback: (value) => `${value}m`,
        },
        grid: {
          color: theme.customTokens?.chartGrid || theme.palette.divider,
        },
      },
    },
  }), [theme]);

  const lineOptions = useMemo(() => ({
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      legend: { display: false },
    },
  }), [baseChartOptions]);

  const multiLineOptions = useMemo(() => ({
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      legend: {
        position: 'bottom',
        labels: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          usePointStyle: true,
          boxWidth: 8,
        },
      },
    },
  }), [baseChartOptions, theme]);

  const doughnutOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          usePointStyle: true,
          boxWidth: 8,
        },
      },
      tooltip: {
        backgroundColor: theme.customTokens?.chartTooltipBg,
        titleColor: theme.customTokens?.chartTooltipText,
        bodyColor: theme.customTokens?.chartTooltipText,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: (context) => `${context.label}: ${Number(context.raw) || 0} min`,
        },
      },
    },
    cutout: '62%',
  }), [theme]);

  const classTrainingLineData = useMemo(() => ({
    labels: classTrainingByPeriod.labels,
    datasets: [
      {
        label: `${selectedClassName} minutes`,
        data: classTrainingByPeriod.values,
        borderColor: theme.customTokens?.lineColor || theme.palette.primary.main,
        backgroundColor: theme.customTokens?.lineFill || theme.palette.primary.main,
        tension: 0.28,
        pointRadius: 3,
        pointHoverRadius: 4,
        pointBackgroundColor: theme.customTokens?.lineColor || theme.palette.primary.main,
        pointBorderColor: theme.palette.background.paper,
        pointBorderWidth: 2,
      },
    ],
  }), [
    classTrainingByPeriod.labels,
    classTrainingByPeriod.values,
    selectedClassName,
    theme,
  ]);

  const classActivityDoughnutData = useMemo(() => ({
    labels: classActivityTotals.labels,
    datasets: [
      {
        label: 'Activity minutes',
        data: classActivityTotals.values,
        backgroundColor: classActivityTotals.values.map((_, index) => chartColors[index % chartColors.length]),
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
    ],
  }), [
    chartColors,
    classActivityTotals.labels,
    classActivityTotals.values,
    theme.palette.background.paper,
  ]);

  const classActivityLineData = useMemo(() => ({
    labels: classActivityTrainingByPeriod.labels,
    datasets: selectedActivitySeries.map(([activityName, totalsByPeriod], index) => ({
      label: activityName,
      data: classActivityTrainingByPeriod.periodKeys.map((periodKey) => totalsByPeriod[periodKey] || 0),
      borderColor: chartColors[index % chartColors.length],
      backgroundColor: chartColors[index % chartColors.length],
      tension: 0.25,
      pointRadius: 2,
      pointHoverRadius: 4,
    })),
  }), [
    chartColors,
    classActivityTrainingByPeriod.labels,
    classActivityTrainingByPeriod.periodKeys,
    selectedActivitySeries,
  ]);

  const classStatsCards = useMemo(() => ([
    {
      label: `${selectedClassName} Total Time`,
      value: `${classStats.totalMinutes} min`,
    },
    {
      label: 'Sessions',
      value: classStats.sessionCount,
    },
    {
      label: 'Average Session',
      value: `${classStats.averageMinutes} min`,
    },
  ]), [classStats, selectedClassName]);

  if (loading) {
    return (
      <Box className="dashboard-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="dashboard-overview dashboard-classes">
      {error && (
        <Alert severity="error" className="dashboard-error">
          {error}
        </Alert>
      )}

      <Paper className="dashboard-activity-drilldown" elevation={0}>
        <Box className="dashboard-activity-drilldown-header">
          <Box className="dashboard-activity-title-block">
          </Box>
          <Box className="dashboard-activity-controls">
            <FormControl className="dashboard-filter-control dashboard-activity-filter" size="small">
              <InputLabel id="dashboard-class-label">Class</InputLabel>
              <Select
                labelId="dashboard-class-label"
                value={selectedClassId}
                label="Class"
                onChange={(event) => setSelectedClassId(event.target.value)}
                disabled={classOptions.length === 0}
              >
                {classOptions.map((trainingClass) => (
                  <MenuItem key={trainingClass.id} value={trainingClass.id}>
                    {trainingClass.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl className="dashboard-filter-control" size="small">
              <InputLabel id="dashboard-class-period-label">Grouping</InputLabel>
              <Select
                labelId="dashboard-class-period-label"
                value={period}
                label="Grouping"
                onChange={(event) => setPeriod(event.target.value)}
              >
                <MenuItem value="week">Weekly</MenuItem>
                <MenuItem value="month" disabled={isMonthlyGroupingDisabled(duration)}>
                  Monthly
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl className="dashboard-filter-control" size="small">
              <InputLabel id="dashboard-class-duration-label">Duration</InputLabel>
              <Select
                labelId="dashboard-class-duration-label"
                value={duration}
                label="Duration"
                onChange={(event) => setDuration(event.target.value)}
              >
                {durationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      <Box className="dashboard-activity-stats dashboard-class-stats">
        {classStatsCards.map((stat) => (
          <Paper key={stat.label} className="dashboard-activity-stat-card" elevation={0}>
            <Typography variant="subtitle2">{stat.label}</Typography>
            <Typography variant="h5" component="p">{stat.value}</Typography>
          </Paper>
        ))}
      </Box>

      <Box className="dashboard-class-chart-grid">
        <DashboardChartCard
          title={`${selectedClassName} Training Trend`}
          isEmpty={classTrainingByPeriod.values.every((value) => value === 0)}
          emptyMessage="No training data found for this class and duration."
          cardClassName="dashboard-class-trend-card"
        >
          <Line data={classTrainingLineData} options={lineOptions} />
        </DashboardChartCard>

        <DashboardChartCard
          title={`${selectedClassName} Activity Mix`}
          isEmpty={classActivityTotals.values.length === 0}
          emptyMessage="No activity data found for this class and duration."
          cardClassName="dashboard-class-mix-card"
        >
          <Doughnut data={classActivityDoughnutData} options={doughnutOptions} />
        </DashboardChartCard>

        <DashboardChartCard
          title={`${selectedClassName} Activities Over Time`}
          isEmpty={classActivityTrainingByPeriod.activities.length === 0 || selectedActivitySeries.length === 0}
          emptyMessage={
            classActivityTrainingByPeriod.activities.length === 0
              ? 'No activity timeline data found for this class and duration.'
              : 'Select at least one activity to show this timeline.'
          }
          cardClassName="dashboard-class-activities-card"
          action={(
            <FormControl className="dashboard-category-filter" size="small">
              <InputLabel id="dashboard-class-activities-filter-label">Activities</InputLabel>
              <Select
                labelId="dashboard-class-activities-filter-label"
                multiple
                value={selectedActivities}
                label="Activities"
                renderValue={(selected) => `${selected.length} selected`}
                onChange={(event) => {
                  const value = event.target.value;
                  setHasCustomizedActivities(true);
                  setSelectedActivities(
                    typeof value === 'string' ? value.split(',') : value
                  );
                }}
              >
                {activityOptions.map((activityName) => (
                  <MenuItem key={activityName} value={activityName}>
                    <Checkbox checked={selectedActivities.includes(activityName)} />
                    <ListItemText primary={activityName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        >
          <Line data={classActivityLineData} options={multiLineOptions} />
        </DashboardChartCard>
      </Box>
    </Box>
  );
}

export default DashboardClasses;
